import { AdminLayout } from "@/components/layout/AdminLayout";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  IconButton,
  useBreakpointValue,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
  Text,
  Tabs,
  TabList,
  Tab,
  useDisclosure,
} from "@chakra-ui/react";
import {
  ChevronLeft,
  Search,
  Funnel,
  ChevronDown,
  ChevronRight,
  MoreVertical,
} from "lucide-react";
import { PatientOverview } from "@/types";
import { userService } from "@/lib/services/userService";
import { Appointment } from "@/types/appointment";
import { PatientCard } from "@/components/card/PatientCard";
import { PatientAboutCard } from "@/components/card/PatientAboutCard";
import { PatientVitalSignsCard } from "@/components/card/PatientVitalSignsCard";
import CancelAppointmentModal from "@/components/modals/CancelAppointmentModal";
import DeleteAppointmentModal from "@/components/modals/DeleteAppointmentModal";
import RescheduleModal from "@/components/modals/RescheduleModal";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { appointmentService } from "@/lib/services/appointmentService";

const PatientProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { showError, showSuccess } = useToastNotification();

  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [patient, setPatient] = useState<PatientOverview | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("appointment_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Reschedule state
  const [rescheduleData, setRescheduleData] = useState({
    requestor: "",
    appointment_date: "",
    appointment_time: "",
    reason: "",
  });
  const [rescheduleErrors, setRescheduleErrors] = useState({});

  // Modals
  const {
    isOpen: isCancelReasonOpen,
    onOpen: onCancelReasonOpen,
    onClose: onCancelReasonClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isRescheduleOpen,
    onOpen: onRescheduleOpen,
    onClose: onRescheduleClose,
  } = useDisclosure();

  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        return setPatient(null);
      }
      try {
        setIsLoading(true);
        const pat = await userService.getPatient(id);
        const patAppointments = await userService.getPatientAppointments(id);
        setPatient(pat);
        setAppointments(patAppointments || []);
      } catch (error) {
        console.error("Failed to fetch patient:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const patientData = location.state;
    if (patientData) {
      setPatient(patientData);
    } else {
      fetchPatient();
    }
  }, [id, location.state]);

  // Handle book appointment
  const handleBookAppointment = () => {
    if (!patient) return;

    navigate(`/appointments/create-appointment?patientId=${patient.id}`, {
      state: {
        patientId: patient.id,
        patientName: patient.user_fullname,
        patientPhone: patient.user_phone_number,
      },
    });
  };

  // Filter and sort appointments
  const filteredAndSortedAppointments = useMemo(() => {
    let filtered = appointments.filter((appointment) => {
      const matchesSearch =
        appointment?.doctor?.user_fullname
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment?.service
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        appointment?.appointment_date
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        appointment?.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = "";
      let bValue: any = "";

      if (sortBy === "appointment_date") {
        aValue = new Date(a?.appointment_date || "").getTime();
        bValue = new Date(b?.appointment_date || "").getTime();
      } else if (sortBy === "doctor") {
        aValue = a?.doctor?.user_fullname || "";
        bValue = b?.doctor?.user_fullname || "";
      } else if (sortBy === "service") {
        aValue = a?.service || "";
        bValue = b?.service || "";
      }

      if (sortBy === "doctor" || sortBy === "service") {
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortOrder === "asc" ? comparison : -comparison;
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [appointments, searchTerm, statusFilter, sortBy, sortOrder]);

  // Pagination
  const paginatedAppointments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedAppointments.slice(
      startIndex,
      startIndex + itemsPerPage,
    );
  }, [filteredAndSortedAppointments, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(
    filteredAndSortedAppointments.length / itemsPerPage,
  );

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    const sortOptions: Record<
      string,
      { sort_by: string; sort_order: "asc" | "desc" }
    > = {
      recent: { sort_by: "appointment_date", sort_order: "desc" },
      "date-asc": { sort_by: "appointment_date", sort_order: "asc" },
      "date-desc": { sort_by: "appointment_date", sort_order: "desc" },
      "doctor-asc": { sort_by: "doctor", sort_order: "asc" },
      "doctor-desc": { sort_by: "doctor", sort_order: "desc" },
      "service-asc": { sort_by: "service", sort_order: "asc" },
      "service-desc": { sort_by: "service", sort_order: "desc" },
    };

    const selected = sortOptions[value];
    if (selected) {
      setSortBy(selected.sort_by);
      setSortOrder(selected.sort_order);
      setCurrentPage(1);
    }
  };

  // Handle appointment actions
  const handleAppointmentAction = (
    appointment: Appointment,
    action: "view" | "reschedule" | "cancel" | "delete",
  ) => {
    setSelectedAppointment(appointment);

    switch (action) {
      case "view":
        navigate(`/appointments/view-appointment/${appointment.id}`);
        break;
      case "reschedule":
        setRescheduleData({
          requestor: patient?.user_fullname || "",
          appointment_date: appointment.appointment_date || "",
          appointment_time: appointment.appointment_time || "",
          reason: "",
        });
        setRescheduleErrors({});
        onRescheduleOpen();
        break;
      case "cancel":
        onCancelReasonOpen();
        break;
      case "delete":
        onDeleteConfirmOpen();
        break;
    }
  };

  // Handle cancel appointment
  const handleCancelWithReason = async () => {
    if (!selectedAppointment || !cancelReason.trim()) {
      showError("Please provide a reason for cancellation");
      return;
    }

    setIsProcessing(true);
    try {
      await appointmentService.cancelAppointment(
        selectedAppointment.id,
        cancelReason,
      );

      showSuccess("Success", "Appointment cancelled successfully");

      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === selectedAppointment.id
            ? { ...apt, status: "Cancelled" }
            : apt,
        ),
      );

      onCancelReasonClose();
      setCancelReason("");
      setSelectedAppointment(null);
    } catch (error: any) {
      showError(error.message || "Failed to cancel appointment");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle delete appointment
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    setIsProcessing(true);
    try {
      await appointmentService.deleteAppointment(selectedAppointment.id);

      showSuccess("Success", "Appointment deleted successfully");

      setAppointments((prev) =>
        prev.filter((apt) => apt.id !== selectedAppointment.id),
      );

      onDeleteConfirmClose();
      setSelectedAppointment(null);
    } catch (error: any) {
      showError(error.message || "Failed to delete appointment");
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle reschedule appointment
  const validateRescheduleForm = () => {
    const errors: any = {};
    if (!rescheduleData.requestor.trim()) errors.requestor = "Required";
    if (!rescheduleData.appointment_date) errors.appointment_date = "Required";
    if (!rescheduleData.appointment_time) errors.appointment_time = "Required";
    if (!rescheduleData.reason.trim()) errors.reason = "Required";

    if (Object.keys(errors).length > 0) {
      setRescheduleErrors(errors);
      return false;
    }
    return true;
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment || !validateRescheduleForm()) return;

    setIsProcessing(true);
    try {
      await appointmentService.rescheduleAppointment(
        selectedAppointment.id,
        rescheduleData,
      );

      showSuccess("Success", "Appointment rescheduled successfully");

      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === selectedAppointment.id
            ? {
                ...apt,
                appointment_date: rescheduleData.appointment_date,
                appointment_time: rescheduleData.appointment_time,
              }
            : apt,
        ),
      );

      onRescheduleClose();
      setRescheduleData({
        requestor: "",
        appointment_date: "",
        appointment_time: "",
        reason: "",
      });
      setRescheduleErrors({});
      setSelectedAppointment(null);
    } catch (error: any) {
      showError(error.message || "Failed to reschedule appointment");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!patient) {
    return (
      <AdminLayout>
        <Box bg="#FEFCF8" minH="100vh" p={{ base: 4, md: 6 }}>
          <Center h="100vh">
            <Spinner size="lg" color="blue.500" />
          </Center>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box bg="#FEFCF8" minH="100vh" p={{ base: 3, sm: 4, md: 6 }}>
        {/* Go Back Button */}
        <Container maxW="container.xl" px={0}>
          <Box
            as="button"
            onClick={() => navigate(-1)}
            fontSize={{ base: "xs", sm: "sm" }}
            fontWeight="medium"
            display="flex"
            alignItems="center"
            gap={1}
            color="gray.600"
            _hover={{ color: "orange.500" }}
            transition="color 0.2s"
            cursor="pointer"
            bg="transparent"
            border="none"
            p={0}
            mb={{ base: 2, md: 4 }}
          >
            <ChevronLeft size={isMobile ? 16 : 20} />
            Go Back
          </Box>
        </Container>

        {/* Main Content Container */}
        <Container maxW="container.xl" px={0}>
          {/* Patient Card */}
          <PatientCard
            patient={patient}
            onBookAppointment={handleBookAppointment}
          />

          {/* About and Vital Signs */}
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={{ base: 4, md: 6 }}
            mt={{ base: 4, md: 6 }}
          >
            {/* About Card */}
            <PatientAboutCard patient={patient} isMobile={isMobile} />

            {/* Vital Signs Card */}
            <PatientVitalSignsCard isMobile={isMobile} />
          </Grid>

          {/* Tabs Section */}
          <Box
            bg="white"
            rounded={{ base: "md", md: "lg" }}
            shadow="sm"
            marginTop={{ base: 4, md: 6 }}
            overflow="hidden"
          >
            <Tabs variant="unstyled" onChange={(index) => setActiveTab(index)}>
              <TabList
                borderBottom="1px"
                borderColor="gray.200"
                overflowX="auto"
                px={{ base: 3, md: 4 }}
                py={1}
              >
                <Tab
                  _selected={{
                    color: "#2E37A4",
                    borderBottom: "2px solid",
                    borderColor: "#2E37A4",
                  }}
                  fontSize={{ base: "xs", sm: "sm" }}
                  fontWeight="medium"
                  whiteSpace="nowrap"
                  px={3}
                  py={3}
                >
                  Appointments
                </Tab>
                <Tab
                  _selected={{
                    color: "#2E37A4",
                    borderBottom: "2px solid",
                    borderColor: "#2E37A4",
                  }}
                  fontSize={{ base: "xs", sm: "sm" }}
                  fontWeight="medium"
                  whiteSpace="nowrap"
                  px={3}
                  py={3}
                >
                  Transactions
                </Tab>
              </TabList>

              {/* Search and Filter Bar */}
              <Flex
                p={{ base: 3, md: 4 }}
                justify="space-between"
                align={{ base: "stretch", sm: "center" }}
                borderBottom="1px"
                borderColor="gray.200"
                flexWrap="wrap"
                gap={3}
                direction={{ base: "column", sm: "row" }}
              >
                <InputGroup maxW={{ base: "100%", sm: "250px", md: "300px" }}>
                  <InputLeftElement pointerEvents="none">
                    <Search size={isMobile ? 14 : 16} color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search appointments..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    width="100%"
                    focusBorderColor="orange.500"
                    height={{ base: "36px", sm: "32px" }}
                    fontSize={{ base: "sm", sm: "md" }}
                    pl={{ base: 8, sm: 10 }}
                  />
                </InputGroup>

                <HStack
                  spacing={2}
                  fontSize="sm"
                  color="gray.600"
                  height={{ base: "36px", sm: "32px" }}
                  w={{ base: "100%", sm: "auto" }}
                  justify={{ base: "space-between", sm: "flex-start" }}
                >
                  {/* Filter Dropdown */}
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      size={{ base: "sm", sm: "sm" }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      leftIcon={<Funnel size={isMobile ? 14 : 16} />}
                      bg="white"
                      px={{ base: 2, sm: 3 }}
                      h="full"
                    >
                      Filter
                    </MenuButton>
                    <MenuList fontSize={{ base: "xs", md: "sm" }}>
                      <MenuItem
                        onClick={() => {
                          setStatusFilter("all");
                          setCurrentPage(1);
                        }}
                      >
                        All Status
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setStatusFilter("Scheduled");
                          setCurrentPage(1);
                        }}
                      >
                        Scheduled
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setStatusFilter("Completed");
                          setCurrentPage(1);
                        }}
                      >
                        Completed
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setStatusFilter("Cancelled");
                          setCurrentPage(1);
                        }}
                      >
                        Cancelled
                      </MenuItem>
                    </MenuList>
                  </Menu>

                  {/* Sort By Dropdown */}
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      size={{ base: "sm", sm: "sm" }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      rightIcon={<ChevronDown size={isMobile ? 14 : 16} />}
                      textColor="gray.900"
                      bg="white"
                      px={{ base: 2, sm: 3 }}
                      h="full"
                    >
                      Sort By
                    </MenuButton>
                    <MenuList fontSize={{ base: "xs", md: "sm" }}>
                      <MenuItem onClick={() => handleSortChange("recent")}>
                        Most Recent
                      </MenuItem>
                      <MenuItem onClick={() => handleSortChange("date-asc")}>
                        Date (Oldest First)
                      </MenuItem>
                      <MenuItem onClick={() => handleSortChange("date-desc")}>
                        Date (Newest First)
                      </MenuItem>
                      <MenuItem onClick={() => handleSortChange("doctor-asc")}>
                        Doctor (A-Z)
                      </MenuItem>
                      <MenuItem onClick={() => handleSortChange("doctor-desc")}>
                        Doctor (Z-A)
                      </MenuItem>
                      <MenuItem onClick={() => handleSortChange("service-asc")}>
                        Service (A-Z)
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleSortChange("service-desc")}
                      >
                        Service (Z-A)
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Flex>

              {/* Appointments Table */}
              {isLoading ? (
                <Center py={8}>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              ) : (
                <>
                  <Box overflowX="auto" px={{ base: 0, md: 2 }}>
                    <Table variant="simple" size={{ base: "sm", md: "md" }}>
                      <Thead bg="white">
                        <Tr>
                          <Th
                            fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                            color="gray.900"
                            fontWeight="600"
                            textTransform="none"
                            whiteSpace="nowrap"
                            px={{ base: 2, sm: 4 }}
                            py={3}
                          >
                            Date & Time
                          </Th>
                          <Th
                            fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                            color="gray.900"
                            fontWeight="600"
                            textTransform="none"
                            whiteSpace="nowrap"
                            px={{ base: 2, sm: 4 }}
                            py={3}
                          >
                            Doctor Name
                          </Th>
                          <Th
                            fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                            color="gray.900"
                            fontWeight="600"
                            textTransform="none"
                            whiteSpace="nowrap"
                            px={{ base: 2, sm: 4 }}
                            py={3}
                            display={{ base: "none", md: "table-cell" }}
                          >
                            Service
                          </Th>
                          <Th
                            fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                            color="gray.900"
                            fontWeight="600"
                            textTransform="none"
                            whiteSpace="nowrap"
                            px={{ base: 2, sm: 4 }}
                            py={3}
                          >
                            Status
                          </Th>
                          <Th px={{ base: 2, sm: 4 }} py={3}></Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {paginatedAppointments.length > 0 ? (
                          paginatedAppointments.map((appointment) => (
                            <Tr
                              key={appointment.id}
                              _hover={{ bg: "gray.50" }}
                              borderBottom="1px"
                              borderColor="gray.100"
                            >
                              <Td
                                fontSize={{ base: "xs", sm: "sm" }}
                                whiteSpace="nowrap"
                                px={{ base: 2, sm: 4 }}
                                py={3}
                              >
                                {appointment.appointment_date || "N/A"}
                              </Td>
                              <Td px={{ base: 2, sm: 4 }} py={3}>
                                <Flex align="center" gap={2}>
                                  <Avatar
                                    size={{ base: "xs", sm: "sm" }}
                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      appointment?.doctor?.user_fullname ||
                                        "Doctor",
                                    )}`}
                                  />
                                  <Box minW={0}>
                                    <Text
                                      fontSize={{ base: "xs", sm: "sm" }}
                                      fontWeight="400"
                                      noOfLines={1}
                                    >
                                      {appointment?.doctor?.user_fullname ||
                                        "N/A"}
                                    </Text>
                                    <Text
                                      fontSize={{ base: "2xs", sm: "xs" }}
                                      color="gray.500"
                                      noOfLines={1}
                                    >
                                      {appointment?.doctor?.department_id
                                        ?.department || "N/A"}
                                    </Text>
                                  </Box>
                                </Flex>
                              </Td>
                              <Td
                                fontSize={{ base: "xs", sm: "sm" }}
                                display={{ base: "none", md: "table-cell" }}
                                px={{ base: 2, sm: 4 }}
                                py={3}
                                noOfLines={1}
                              >
                                {appointment.service || "N/A"}
                              </Td>
                              <Td px={{ base: 2, sm: 4 }} py={3}>
                                <Badge
                                  color={
                                    appointment.status === "Completed"
                                      ? "#27AE60"
                                      : appointment.status === "Scheduled"
                                        ? "#2F80ED"
                                        : "#EF1E1E"
                                  }
                                  bg={
                                    appointment.status === "Completed"
                                      ? "green.50"
                                      : appointment.status === "Scheduled"
                                        ? "blue.50"
                                        : "red.50"
                                  }
                                  border="1px"
                                  borderColor={
                                    appointment.status === "Completed"
                                      ? "green.100"
                                      : appointment.status === "Scheduled"
                                        ? "blue.100"
                                        : "red.100"
                                  }
                                  px={2}
                                  py={1}
                                  rounded="md"
                                  fontSize={{ base: "2xs", sm: "xs" }}
                                  fontWeight="medium"
                                  textTransform="none"
                                  whiteSpace="nowrap"
                                >
                                  {appointment.status || "N/A"}
                                </Badge>
                              </Td>
                              <Td
                                textAlign="right"
                                px={{ base: 2, sm: 4 }}
                                py={3}
                              >
                                <Menu>
                                  <MenuButton
                                    as={IconButton}
                                    aria-label="More options"
                                    icon={
                                      <MoreVertical size={isMobile ? 14 : 16} />
                                    }
                                    variant="ghost"
                                    size={{ base: "xs", sm: "sm" }}
                                    minW="auto"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <MenuList fontSize={{ base: "xs", md: "sm" }}>
                                    <MenuItem
                                      onClick={() =>
                                        handleAppointmentAction(
                                          appointment,
                                          "view",
                                        )
                                      }
                                    >
                                      View Details
                                    </MenuItem>
                                    {appointment.status === "Scheduled" && (
                                      <MenuItem
                                        onClick={() =>
                                          handleAppointmentAction(
                                            appointment,
                                            "reschedule",
                                          )
                                        }
                                      >
                                        Reschedule
                                      </MenuItem>
                                    )}
                                    {appointment.status === "Scheduled" && (
                                      <MenuItem
                                        onClick={() =>
                                          handleAppointmentAction(
                                            appointment,
                                            "cancel",
                                          )
                                        }
                                      >
                                        Cancel Appointment
                                      </MenuItem>
                                    )}
                                    <MenuItem
                                      color="red.600"
                                      onClick={() =>
                                        handleAppointmentAction(
                                          appointment,
                                          "delete",
                                        )
                                      }
                                    >
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td
                              colSpan={5}
                              textAlign="center"
                              py={8}
                              color="gray.500"
                              px={{ base: 2, sm: 4 }}
                            >
                              No appointments found.
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Pagination */}
                  {filteredAndSortedAppointments.length > 0 && (
                    <Box
                      px={{ base: 3, sm: 4, md: 6 }}
                      py={4}
                      borderTop="1px"
                      borderColor="gray.100"
                    >
                      <Flex
                        direction={{ base: "column", sm: "row" }}
                        justify="space-between"
                        align="center"
                        gap={{ base: 3, sm: 0 }}
                      >
                        <Text fontSize="sm" color="gray.600">
                          Showing <b>{paginatedAppointments.length}</b> from{" "}
                          <b>{filteredAndSortedAppointments.length}</b>{" "}
                          appointments
                        </Text>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<ChevronLeft size={16} />}
                            aria-label="Previous"
                            variant="outline"
                            size="sm"
                            isDisabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                          />
                          {Array.from(
                            { length: Math.min(5, totalPages) },
                            (_, i) => i + 1,
                          ).map((page) => (
                            <Button
                              key={page}
                              size="sm"
                              variant={
                                currentPage === page ? "solid" : "outline"
                              }
                              colorScheme={
                                currentPage === page ? "red" : "gray"
                              }
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </Button>
                          ))}
                          {totalPages > 5 && (
                            <>
                              <Text fontSize="sm" color="gray.600">
                                ...
                              </Text>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePageChange(totalPages)}
                              >
                                {totalPages}
                              </Button>
                            </>
                          )}
                          <IconButton
                            icon={<ChevronRight size={16} />}
                            aria-label="Next"
                            variant="outline"
                            size="sm"
                            isDisabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                          />
                        </HStack>
                      </Flex>
                    </Box>
                  )}
                </>
              )}
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Cancel Appointment Modal */}
      <CancelAppointmentModal
        isOpen={isCancelReasonOpen}
        onClose={onCancelReasonClose}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onConfirm={handleCancelWithReason}
        isProcessing={isProcessing}
      />

      {/* Delete Appointment Modal */}
      <DeleteAppointmentModal
        isOpen={isDeleteConfirmOpen}
        onClose={onDeleteConfirmClose}
        onConfirm={handleDeleteAppointment}
        isProcessing={isProcessing}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleOpen}
        onClose={onRescheduleClose}
        rescheduleData={rescheduleData}
        setRescheduleData={setRescheduleData}
        rescheduleErrors={rescheduleErrors}
        handleRescheduleAppointment={handleRescheduleAppointment}
        isProcessing={isProcessing}
      />
    </AdminLayout>
  );
};

export default PatientProfilePage;
